import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Users, ShieldCheck, ArrowRight, Flag, Link2, Check } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ShareOnX } from "@/components/ShareOnX";
import { api, formatApiErrorDetail } from "@/lib/api";

const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2";
const inputCls = "w-full border border-slate-300 bg-white px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] focus:ring-1 focus:ring-[#D90429] transition-colors duration-200";

export default function ChapterPage() {
  const { slug } = useParams();
  const [chapter, setChapter] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", zip_code: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success("Link copied — share it with your network!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Copy failed"));
  };

  useEffect(() => {
    api.get(`/chapter/${slug}`)
      .then((r) => setChapter(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/chapter/${slug}/founding-members`, form);
      setDone(true);
      setChapter((c) => ({ ...c, founding_count: data.founding_count }));
      if (data.goal_reached) {
        const fire = (ratio, opts) => confetti({ ...opts, origin: { y: 0.6 }, particleCount: Math.floor(200 * ratio), colors: ["#D90429", "#F4A261", "#ffffff", "#0A1128"] });
        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.35, { spread: 60 });
        fire(0.25, { spread: 100, decay: 0.91, scalar: 0.9 });
        fire(0.15, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        setTimeout(() => confetti({ particleCount: 120, spread: 100, origin: { y: 0.5 }, colors: ["#D90429", "#F4A261", "#ffffff"] }), 400);
        toast.success("🎉 Goal reached! You put this chapter over the top!");
      } else {
        toast.success("Welcome, Founding Member!");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div>
        <Navbar />
        <section className="pt-40 pb-40 bg-muted text-center px-5">
          <h1 className="font-display text-4xl uppercase">Chapter Not Found</h1>
          <p className="text-slate-600 mt-3">This chapter link is invalid or has been removed.</p>
          <Link to="/chapters" className="inline-flex items-center gap-2 mt-8 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200">Browse Chapters</Link>
        </section>
        <Footer />
      </div>
    );
  }

  if (!chapter) {
    return (
      <div>
        <Navbar />
        <div className="pt-40 pb-40 text-center text-slate-400">Loading…</div>
        <Footer />
      </div>
    );
  }

  const label = chapter.name || `${chapter.city ? chapter.city + ", " : ""}${chapter.state}`;

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-16 bg-[#0A1128] text-white grain relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-[11px] uppercase tracking-[0.2em] px-3 py-1 ${chapter.status === "Forming" ? "bg-[#F4A261]/20 text-[#F4A261]" : "bg-[#D90429] text-white"}`}>
              {chapter.status || "Active"} Chapter
            </span>
            {chapter.goal_reached && (
              <span data-testid="goal-reached-badge" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] px-3 py-1 bg-gradient-to-r from-[#D90429] to-[#F4A261] text-white font-bold">
                Founding Goal Reached 🎉
              </span>
            )}
          </div>
          <h1 data-testid="chapter-page-title" className="font-display text-5xl sm:text-6xl uppercase mt-4 leading-[0.95]">{label}</h1>
          <div className="flex items-center gap-2 text-white/70 mt-4">
            <MapPin className="w-4 h-4 text-[#F4A261]" /> {chapter.city ? `${chapter.city}, ` : ""}{chapter.state}
          </div>
          <div className="flex items-center gap-3 mt-8" data-testid="founding-count">
            <div className="w-14 h-14 bg-[#D90429] flex items-center justify-center"><Users className="w-6 h-6" /></div>
            <div>
              <div className="font-display text-4xl leading-none">{chapter.founding_count}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">Founding Members</div>
            </div>
          </div>

          {/* Founder goal progress */}
          {(() => {
            const goal = chapter.founding_goal || 50;
            const pct = Math.min(100, Math.round((chapter.founding_count / goal) * 100));
            const reached = chapter.founding_count >= goal;
            return (
              <div className="mt-6 max-w-lg" data-testid="founder-goal-bar">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#F4A261] font-semibold">
                    {reached ? "Goal Reached!" : `First ${goal} Founders`}
                  </span>
                  <span className="text-xs text-white/60">{chapter.founding_count} / {goal}</span>
                </div>
                <div className="h-3 bg-white/10 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-[#D90429] to-[#F4A261]"
                    data-testid="founder-goal-fill"
                  />
                </div>
                <p className="text-xs text-white/50 mt-2">
                  {reached
                    ? "This chapter hit its founding goal — momentum is real."
                    : `${goal - chapter.founding_count} more to hit the founding goal. Recruit your network.`}
                </p>
              </div>
            );
          })()}

          {/* Share buttons */}
          <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="chapter-share-buttons">
            <ShareOnX
              testid="chapter-share-x"
              text={`I'm backing the ${label} chapter of Patriot Power USA. Stand with us — become a Founding Member:`}
              url={shareUrl}
              className="inline-flex items-center gap-2 bg-white text-[#0A1128] px-5 py-2.5 uppercase tracking-[0.12em] text-xs font-bold hover:bg-white/90 transition-colors duration-200"
            />
            <button
              type="button"
              data-testid="chapter-copy-link"
              onClick={copyLink}
              className="inline-flex items-center gap-2 border border-white/30 text-white px-5 py-2.5 uppercase tracking-[0.12em] text-xs font-bold hover:bg-white/10 transition-colors duration-200"
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Link2 className="w-3.5 h-3.5" /> Copy Link</>}
            </button>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 bg-muted">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-3xl uppercase">Be A Founding Member</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Founding Members are the first patriots to stand up and be counted in {chapter.state}. You'll be first
              in line for meetings, training, and the fight to protect your community. Add your name to the roster
              and help build this chapter from the ground up.
            </p>
            <ul className="mt-6 space-y-3 text-slate-700">
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#D90429]" /> Recognized as a chapter founder</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#D90429]" /> Priority access to training & events</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#D90429]" /> Direct line to your chapter leader</li>
            </ul>
            {(chapter.contact_email || chapter.contact_phone) && (
              <div className="mt-8 pt-6 border-t border-slate-200 space-y-2 text-sm text-slate-600">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Chapter Contact</div>
                {chapter.contact_email && <a href={`mailto:${chapter.contact_email}`} className="flex items-center gap-2 hover:text-[#D90429]"><Mail className="w-4 h-4" /> {chapter.contact_email}</a>}
                {chapter.contact_phone && <a href={`tel:${chapter.contact_phone}`} className="flex items-center gap-2 hover:text-[#D90429]"><Phone className="w-4 h-4" /> {chapter.contact_phone}</a>}
              </div>
            )}
          </div>

          <div>
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="founding-success" className="bg-white border border-slate-200 p-10 text-center h-full flex flex-col justify-center">
                <Flag className="w-14 h-14 text-[#D90429] mx-auto mb-5" />
                <h3 className="font-display text-2xl uppercase">You're In, Patriot</h3>
                <p className="text-slate-600 mt-3">You're now a Founding Member of the {label} chapter. Check your email for confirmation.</p>
                <Link to="/" className="inline-flex items-center justify-center gap-2 mt-6 text-[#D90429] uppercase tracking-[0.15em] text-sm font-bold hover:opacity-70">Explore the mission <ArrowRight className="w-4 h-4" /></Link>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="founding-form" className="bg-white border border-slate-200 p-6 sm:p-8">
                <div className="space-y-5">
                  <div>
                    <label className={labelCls}>Full Name <span className="text-[#D90429]">*</span></label>
                    <input data-testid="founding-name" name="name" value={form.name} onChange={onChange} required className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Email <span className="text-[#D90429]">*</span></label>
                    <input data-testid="founding-email" type="email" name="email" value={form.email} onChange={onChange} required className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Phone</label>
                      <input data-testid="founding-phone" name="phone" value={form.phone} onChange={onChange} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>ZIP</label>
                      <input data-testid="founding-zip" name="zip_code" value={form.zip_code} onChange={onChange} className={inputCls} />
                    </div>
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="founding-submit" className="w-full mt-6 bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60">
                  {loading ? "Joining…" : "Become a Founding Member"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
